import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { 
  Card, 
  CardBody, 
  Table, 
  TableHeader, 
  TableColumn, 
  TableBody, 
  TableRow, 
  TableCell,
  Chip,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faTasks,
  faUser,
  faCalendar,
  faChartLine,
  faTrash,
  faExclamationTriangle,
  faEdit
} from "@fortawesome/free-solid-svg-icons";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import { getDataWithFilterUrl } from "../../controllers/APIController";
import CreateTaskModal from "./CreateTaskModal";
import { PageLoader } from "../../components/Loader";
import TaskWorkflowView from "../../components/TaskWorkflowView";
import { StandardCancelButton, StandardDeleteButton } from "../../components/StandardButtons";

const MetricTaskDetails = () => {
  const { metricId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [metric, setMetric] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false since we have data
  const [error, setError] = useState(null);
  
  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    console.log("Navigation state received:", location.state);
    console.log("Current metricId:", metricId);
    
    // Get metric data and tasks from navigation state
    if (location.state?.metricData) {
      setMetric(location.state.metricData);
      console.log("Metric data set:", location.state.metricData);
    }
    
    if (location.state?.tasksForMetric && Array.isArray(location.state.tasksForMetric)) {
      setTasks(location.state.tasksForMetric);
      console.log(`Using ${location.state.tasksForMetric.length} tasks from navigation state:`, location.state.tasksForMetric);
    } else if (location.state?.metricData?.tasks && Array.isArray(location.state.metricData.tasks)) {
      // Fallback: try to get tasks from the metric data itself
      setTasks(location.state.metricData.tasks);
      console.log(`Using ${location.state.metricData.tasks.length} tasks from metric data:`, location.state.metricData.tasks);
    } else {
      console.log("No tasks passed in navigation state or tasks is not an array, falling back to API call");
      console.log("Tasks received:", location.state?.tasksForMetric);
      console.log("Metric data tasks:", location.state?.metricData?.tasks);
      // Fallback: try to fetch if no data passed
      fetchTasksForMetric();
    }
  }, [metricId, location.state]);

  const fetchTasksForMetric = async () => {
    try {
      setLoading(true);
      console.log("Fetching tasks for metric:", metricId);
      
      // Filter tasks by the specific metric
      const filterUrl = `filters[metric][$eqi]=${metricId}`;
      const response = await getDataWithFilterUrl("tasks", filterUrl);
      
      if (response && response.data) {
        setTasks(response.data);
        console.log(`Found ${response.data.length} tasks for metric ${metricId}`);
      } else {
        setTasks([]);
        console.log("No tasks found for this metric");
      }
    } catch (error) {
      console.error("Error fetching tasks for metric:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'under_review': return 'warning';
      case 'not_started': return 'default';
      case 'on_hold': return 'danger';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Handle delete task
  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!taskToDelete) return;
    
    setDeleteLoading(true);
    try {
      // TODO: Implement actual delete API call
      console.log("Deleting task:", taskToDelete.id);
      
      // For now, just remove from local state
      setTasks(prev => prev.filter(t => t.id !== taskToDelete.id));
      
      // Close modal and reset state
      setIsDeleteModalOpen(false);
      setTaskToDelete(null);
      
      // Show success message (you can add a toast notification here)
      console.log("Task deleted successfully");
      
    } catch (error) {
      console.error("Error deleting task:", error);
      // Show error message
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setTaskToDelete(null);
  };

  // Handle edit task
  const handleEditClick = (task) => {
    setTaskToEdit(task);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    // Refresh the task list or update the specific task
    // For now, just close the modal
    setIsEditModalOpen(false);
    setTaskToEdit(null);
    
    // TODO: Refresh tasks or update the specific task in the list
    console.log("Task updated successfully");
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
    setTaskToEdit(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };



  if (loading) {
    return (
      <PageLoader
        loadingText="Loading tasks..."
        description="Fetching task details for this metric"
        variant="fullscreen"
      />
    );
  }

  if (error) {
    return (
      <PageLoader
        error={error}
        onRetry={fetchTasksForMetric}
        variant="fullscreen"
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-white to-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">
                  {metric?.name || 'Metric Tasks'}
                </h1>
                <p className="text-slate-600 mt-1">
                  {metric?.description || 'Task details for this metric'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <FontAwesomeIcon icon={faTasks} className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-600">
                  {tasks.length}
                </div>
                <div className="text-sm text-emerald-700">
                  {tasks.length === 1 ? 'Task' : 'Tasks'} Assigned
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 py-2">
        <BreadCrumbsPage />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {tasks.length === 0 ? (
                      <div className="text-center py-12">
              <div className="text-slate-400 text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">No tasks found</h3>
              <p className="text-slate-600 mb-4">This metric doesn't have any tasks assigned yet.</p>
            </div>
        ) : (
          <Card className="border border-slate-200 shadow-sm bg-gradient-to-br from-white to-slate-50">
            <CardBody className="p-0">
              <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-emerald-900">Task Details</span>
                    <span className="text-sm text-emerald-600">All tasks for this metric</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Chip
                      size="sm"
                      color="success"
                      variant="flat"
                      className="text-xs"
                    >
                      {tasks.filter(t => t.attributes?.Status === 'completed').length}/{tasks.length} Completed
                    </Chip>
                    <span className="text-xs text-slate-500">
                      {Math.round((tasks.filter(t => t.attributes?.Status === 'completed').length / tasks.length) * 100)}% completion
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <Table
                  removeWrapper
                  selectionMode="single"
                  aria-label="tasks-table"
                  classNames={{
                    th: "bg-slate-50 text-slate-700 font-semibold text-xs border-b border-slate-100 py-2",
                    td: "py-2 border-b border-slate-100 text-xs",
                  }}
                >
                  <TableHeader>
                    <TableColumn>Task Name</TableColumn>
                    <TableColumn>Status</TableColumn>
                    <TableColumn>Priority</TableColumn>
                    <TableColumn>Assigned To</TableColumn>
                    <TableColumn>Due Date</TableColumn>
                    <TableColumn>Progress</TableColumn>
                    <TableColumn>Workflow</TableColumn>
                    <TableColumn>Actions</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {tasks.map((task) => {
                      const taskData = task.attributes || task;
                      const assignedUser = taskData.assignedTo?.data?.attributes || taskData.assignedTo;
                      
                      return (
                        <TableRow 
                          key={task.id} 
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => handleEditClick(task)}
                        >
                          <TableCell>
                            <div>
                              <div className="font-medium text-slate-900">{taskData.Name || 'Unnamed Task'}</div>
                              <div className="text-xs text-slate-500 line-clamp-2">{taskData.Description || 'No description'}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="sm"
                              color={getStatusColor(taskData.Status)}
                              variant="flat"
                              className="text-xs"
                            >
                              {taskData.Status ? taskData.Status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A'}
                            </Chip>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="sm" 
                              color={getPriorityColor(taskData.Priority)} 
                              variant="flat"
                              className="text-xs"
                            >
                              {taskData.Priority ? taskData.Priority.charAt(0).toUpperCase() + taskData.Priority.slice(1) : 'N/A'}
                            </Chip>
                          </TableCell>
                          <TableCell className="text-slate-700">
                            {assignedUser ? (
                              typeof assignedUser.username === 'string' ? assignedUser.username :
                              typeof assignedUser.email === 'string' ? assignedUser.email :
                              typeof assignedUser === 'string' ? assignedUser : 'N/A'
                            ) : 'Not Assigned'}
                          </TableCell>
                          <TableCell className="text-slate-700">
                            {formatDate(taskData.EstimatedEndDate)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-slate-200 rounded-full h-2">
                                <div 
                                  className="bg-emerald-500 h-2 rounded-full" 
                                  style={{ width: `${taskData.Progress || 0}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-slate-600">{taskData.Progress || 0}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-xs">
                              <TaskWorkflowView
                                task={taskData}
                                onUpdate={(taskId, result) => {
                                  console.log('Workflow action completed:', result);
                                  // Refresh the task list
                                  fetchTasksForMetric();
                                }}
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                color="primary"
                                variant="flat"
                                className="text-xs"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  handleEditClick(task);
                                }}
                                startContent={<FontAwesomeIcon icon={faEdit} />}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                variant="flat"
                                className="text-xs"
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click
                                  handleDeleteClick(task);
                                }}
                                startContent={<FontAwesomeIcon icon={faTrash} />}
                              >
                                Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={handleDeleteCancel}>
        <ModalContent>
          <ModalHeader className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Delete Task</h3>
              <p className="text-sm text-slate-600">This action cannot be undone</p>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">
                  Are you sure you want to delete the task <strong>"{taskToDelete?.attributes?.Name || taskToDelete?.Name || 'Unnamed Task'}"</strong>?
                </p>
                <p className="text-red-700 text-xs mt-2">
                  This will permanently remove the task and all its associated data.
                </p>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <StandardCancelButton onPress={handleDeleteCancel}>
              Cancel
            </StandardCancelButton>
            <StandardDeleteButton
              onPress={handleDeleteConfirm}
              isLoading={deleteLoading}
              startContent={!deleteLoading && <FontAwesomeIcon icon={faTrash} />}
            >
              {deleteLoading ? "Deleting..." : "Delete Task"}
            </StandardDeleteButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Task Modal */}
      <CreateTaskModal
        isOpen={isEditModalOpen}
        onClose={handleEditCancel}
        onSuccess={handleEditSuccess}
        editMode={true}
        existingTask={taskToEdit}
      />
    </div>
  );
};

export default MetricTaskDetails;
